<template>
  <el-dialog
    class="accept-refuse-sample-dialog"
    visible
    :show-close="false"
    @close="abortAction"
  >
    <ContentHeader
      slot="title"
      :title="
        isAccept ? $t('quarantine.accept.title') : $t('quarantine.refuse.title')
      "
    />
    <ValidationObserver v-slot="{ handleSubmit, invalid }">
      <el-form
        ref="form"
        label-position="top"
      >
        <el-row :gutter="20">
          <el-col :span="24">
            <p
              v-if="isAccept"
              class="accept-refuse-title"
            >
              {{ $t('quarantine.accept.description') }}
            </p>
            <p
              v-else
              class="accept-refuse-title"
            >
              {{ $t('quarantine.refuse.description') }}
            </p>
          </el-col>
        </el-row>
        <el-row
          v-if="isAccept && !sampleIsExternal"
          :gutter="20"
        >
          <el-col :span="24">
            <FormItem
              rules="required"
              :label="$t('quarantine.accept.sampleName')"
            >
              <el-autocomplete
                v-model="formData.sampleName"
                style="width: 100%"
                :fetch-suggestions="findSamplesByCode"
                :trigger-on-focus="false"
                value-key="label"
                clearable
                placeholder=""
                @select="selectSample"
                @change="emptySelectedSample"
              >
                <template slot-scope="{ item }">
                  <AutocompleteItem :item="item" />
                </template>
              </el-autocomplete>
            </FormItem>
          </el-col>
        </el-row>
        <el-row
          v-if="!isAccept && sampleIsExternal"
          :gutter="20"
        >
          <el-col :span="24">
            <FormItem
              rules="required"
              :label="$t('quarantine.refuse.rejectReason')"
            >
              <el-input
                type="textarea"
                v-model="formData.rejectReason"
              />
            </FormItem>
          </el-col>
        </el-row>
        <FormButtons>
          <el-button
            type="secondary"
            @click.prevent="abortAction"
          >
            {{ $t('quarantine.accept.abort') }}
          </el-button>
          <el-button
            v-if="isAccept"
            type="primary"
            native-type="submit"
            :disabled="invalid"
            @click.prevent="handleSubmit(acceptRefuseSample)"
          >
            {{ $t('quarantine.accept.confirm') }}
          </el-button>
          <el-button
            v-else
            type="primary"
            native-type="submit"
            @click.prevent="handleSubmit(acceptRefuseSample)"
          >
            {{ $t('quarantine.accept.confirm') }}
          </el-button>
        </FormButtons>
      </el-form>
    </ValidationObserver>
  </el-dialog>
</template>

<script>
  import { quarantineApi } from '@/api';
  import FormItem from '@/components/FormItem.vue';
  import ContentHeader from '@/components/ContentHeader.vue';
  import FormButtons from '@/components/FormButtons.vue';
  import AutocompleteItem from '@/components/AutocompleteItem.vue';

  const defaultData = {
    rejectReason: null,
    sampleCode: null,
    sampleName: null,
  };

  export default {
    name: 'AcceptRefuseSample',
    components: {
      FormItem,
      ContentHeader,
      FormButtons,
      AutocompleteItem,
    },
    props: {
      isAccept: { type: Boolean, required: true },
      sample: { type: Object, required: true },
    },
    computed: {
      sampleIsExternal() {
        return this.sample.labSampleId !== null;
      },
    },
    data() {
      return {
        formData: { ...defaultData },
      };
    },
    methods: {
      async findSamplesByCode(text, cb) {
        if (text.length >= 3) {
          const response = await quarantineApi.autocompleteFetch(text);
          const samples = response.data.map((s) => ({
            value: s.id,
            label: `${s.groupingId} - ${s.productName}`,
          }));
          cb(samples);
        } else {
          cb([]);
        }
      },
      selectSample(sample) {
        this.formData.sampleCode = sample.value;
      },
      emptySelectedSample() {
        this.formData.sampleCode = null;
      },
      async acceptRefuseSample() {
        const { id } = this.sample;
        const labSampleId = this.sample.labSampleId || this.formData.sampleCode;
        try {
          if (this.isAccept) {
            await quarantineApi.accept(id, labSampleId);
          } else if (!this.isAccept && this.sampleIsExternal) {
            await quarantineApi.reject(
              id,
              labSampleId,
              this.formData.rejectReason
            );
          } else if (!this.isAccept && !this.sampleIsExternal) {
            await quarantineApi.delete(id);
          }
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('quarantine.update.success'),
          });
          this.$emit('dialogClose', { success: true });
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      abortAction() {
        this.formData = { ...defaultData };
        this.$emit('dialogClose', { success: false });
      },
    },
  };
</script>

<style lang="scss">
  .accept-refuse-sample-dialog {
    .el-dialog {
      width: 30%;
    }

    .accept-refuse-title {
      padding-bottom: 1rem;
    }
  }
</style>
